import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * ExitPresentationButton
 *
 * A whisper-quiet control that lets presenters leave the running
 * presentation and return to the Presenter Dashboard (edit agenda,
 * download PDF, etc.).
 *
 * "Silent luxury" behaviour:
 *   • Barely visible by default (very low opacity)
 *   • Softly fades in on any mouse movement or hover
 *   • Fades back out again after ~2.5s of stillness
 *   • Also reachable via the ESC key (handled in PresentationPage)
 */
export default function ExitPresentationButton() {
  const navigate = useNavigate();

  const label = 'Dashboard';
  const hint = 'ESC';

  return (
    <button
      type="button"
      data-testid="exit-presentation-btn"
      aria-label={label}
      title={`${label}  ·  ${hint}`}
      onClick={() => {
        if (document.fullscreenElement) {
          document.exitFullscreen?.().catch?.(() => {});
        }
        navigate('/dashboard');
      }}
      style={{
        position: 'fixed',
        top: 18,
        right: 160,
        zIndex: 101,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 14px',
        borderRadius: 999,
        background: 'rgba(10, 10, 12, 0.55)',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        color: 'rgba(255, 255, 255, 0.82)',
        fontFamily:
          '"Inter", ui-sans-serif, system-ui, -apple-system, sans-serif',
        fontSize: 12,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        cursor: 'pointer',
        opacity: 0,
        transition:
          'opacity 300ms ease, background 300ms ease, border-color 300ms ease',
        userSelect: 'none',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.opacity = '1';
        e.currentTarget.style.background = 'rgba(20, 20, 22, 0.75)';
        e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.55)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.opacity = '0';
        e.currentTarget.style.background = 'rgba(10, 10, 12, 0.55)';
        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
      }}
    >
      <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        aria-hidden="true"
      >
        <line x1="2" y1="2" x2="10" y2="10" />
        <line x1="10" y1="2" x2="2" y2="10" />
      </svg>
      <span>{label}</span>
      <span
        style={{
          fontSize: 10,
          opacity: 0.55,
          border: '1px solid rgba(255,255,255,0.25)',
          borderRadius: 4,
          padding: '1px 5px',
          letterSpacing: '0.1em',
        }}
      >
        {hint}
      </span>
    </button>
  );
}
